import moment, { Moment } from 'moment';
import { PlatformEphemerisTableResponse } from '@data/_generated-api/platform-api/models/platform-ephemeris-table-response';
import { PlatformEphemerisDailyResponse } from '@data/_generated-api/platform-api/models/platform-ephemeris-daily-response';
import { PlatformEphemerisDailyPositionDto } from '@data/_generated-api/platform-api/models/platform-ephemeris-daily-position-dto';
import { PlatformEphemerisMomentResponse } from '@data/_generated-api/platform-api/models/platform-ephemeris-moment-response';
import { HdChartPointKey } from '../chart/bodygraph/planet-grid/hd-planet-display-config';

/**
 * Ephemeris-pagina (spec `docs/superpowers/specs/2026-10-13-ephemeris-pagina-design.md`):
 * modellen rond `GET /api/ephemeris/table`.
 */

/** Kolomsleutels zoals de API ze levert (Maia sourceKeys + `lilith`), in vaste volgorde. */
export const EPHEMERIS_BODY_KEYS = [
  'sun', 'earth', 'north-node', 'south-node', 'moon', 'mercury', 'venus', 'mars',
  'jupiter', 'saturn', 'uranus', 'neptune', 'pluto', 'chiron', 'lilith',
] as const;

export type EphemerisBodyKey = (typeof EPHEMERIS_BODY_KEYS)[number];

/** API-sleutel → sleutel van de planeet-info/display-config (naam, glyph). */
export const EPHEMERIS_CHART_POINT_KEYS: Readonly<Record<EphemerisBodyKey, HdChartPointKey>> = {
  sun: 'sun',
  earth: 'earth',
  'north-node': 'northNode',
  'south-node': 'southNode',
  moon: 'moon',
  mercury: 'mercury',
  venus: 'venus',
  mars: 'mars',
  jupiter: 'jupiter',
  saturn: 'saturn',
  uranus: 'uranus',
  neptune: 'neptune',
  pluto: 'pluto',
  chiron: 'chiron',
  lilith: 'lilith',
};

export function isEphemerisBodyKey(key: string): key is EphemerisBodyKey {
  return (EPHEMERIS_BODY_KEYS as readonly string[]).includes(key);
}

export interface EphemerisBodyState {
  readonly gate: number;
  readonly line: number;
  readonly retrograde: boolean;
}

/** Wijziging van één lichaam: de volledige stand ná het moment. */
export interface EphemerisChange extends EphemerisBodyState {
  /** Index in {@link EphemerisTable.bodies}. */
  readonly body: number;
  readonly kind: 'line' | 'station';
}

export interface EphemerisTableRow {
  readonly atUtc: Moment;
  readonly changes: readonly EphemerisChange[];
  /**
   * Lengte van elk lichaam bij deze rij (zelfde volgorde als `bodies`); een gewijzigd lichaam
   * 1 s na zijn kruising gemeten, dus altijd in de getoonde lijn. Voor de tooltips.
   */
  readonly longitudes: readonly number[] | undefined;
}

export interface EphemerisTable {
  readonly fromUtc: Moment;
  readonly toUtc: Moment;
  /** Vroegst toegelaten moment van de ephemeris. */
  readonly minUtc: Moment;
  /** Laatst toegelaten moment (exclusief). */
  readonly maxUtc: Moment;
  readonly maxWindowDays: number;
  readonly bodies: readonly EphemerisBodyKey[];
  /** Stand per lichaam op `fromUtc`, zelfde volgorde als `bodies`. */
  readonly initial: readonly EphemerisBodyState[];
  /** Lengte van elk lichaam op `fromUtc` (tooltips van de beginrij). */
  readonly initialLongitudes: readonly number[] | undefined;
  readonly rows: readonly EphemerisTableRow[];
}

/**
 * DTO → model. Gooit bij een onvolledige of onbekende response: de kolomindexen van de
 * wijzigingen zijn dan niet te vertrouwen, dus liever een foutstatus dan een verkeerde tabel.
 */
export function toEphemerisTable(dto: PlatformEphemerisTableResponse): EphemerisTable {
  const bodies = (dto.bodies ?? []).map(body => body.key ?? '');
  if (bodies.length === 0 || !bodies.every(isEphemerisBodyKey)) {
    throw new Error(`Onbekende ephemeris-kolommen: ${bodies.join(', ')}`);
  }
  const initial = dto.initial ?? [];
  if (initial.length !== bodies.length) {
    throw new Error('Ephemeris-beginstand past niet bij de kolommen');
  }

  return {
    fromUtc: moment.utc(dto.fromUtc),
    toUtc: moment.utc(dto.toUtc),
    minUtc: moment.utc(dto.minUtc),
    maxUtc: moment.utc(dto.maxUtc),
    maxWindowDays: dto.maxWindowDays ?? 31,
    bodies,
    initial: initial.map(state => ({
      gate: state.gate ?? 0,
      line: state.line ?? 0,
      retrograde: state.retrograde ?? false,
    })),
    initialLongitudes: longitudesFor(dto.initialLongitudes, bodies.length),
    rows: (dto.rows ?? []).map(row => ({
      atUtc: moment.utc(row.atUtc),
      longitudes: longitudesFor(row.longitudes, bodies.length),
      changes: (row.changes ?? []).map(change => ({
        body: change.body ?? 0,
        kind: change.kind === 'station' ? 'station' : 'line',
        gate: change.gate ?? 0,
        line: change.line ?? 0,
        retrograde: change.retrograde ?? false,
      })),
    })),
  };
}

/** Positie van één lichaam op één moment: graad + afgeleide HD-activatie. */
export interface EphemerisPosition {
  /** Geocentrische tropische lengte in graden, [0, 360). */
  readonly longitude: number;
  readonly retrograde: boolean;
  readonly gate: number;
  readonly line: number;
  readonly color: number;
  readonly tone: number;
  readonly base: number;
}

/** Eén rij van de dagposities: alle lichamen op `atUtc`, de Maan ook `moonOffsetHours` later. */
export interface EphemerisDay {
  readonly atUtc: Moment;
  /** Zelfde volgorde als {@link EphemerisDailyTable.bodies}. */
  readonly positions: readonly EphemerisPosition[];
  readonly moonLater: EphemerisPosition;
}

export interface EphemerisDailyTable {
  readonly fromUtc: Moment;
  readonly toUtc: Moment;
  readonly minUtc: Moment;
  readonly maxUtc: Moment;
  readonly maxWindowDays: number;
  /** Uren tussen twee rijen (12: 00:00 en 12:00). */
  readonly stepHours: number;
  /** De extra Maanpositie valt zoveel uur na elke rij (6). */
  readonly moonOffsetHours: number;
  /** IANA-tijdzone waarin de rijen op 00:00 en 12:00 vallen. */
  readonly timeZone: string;
  readonly bodies: readonly EphemerisBodyKey[];
  readonly days: readonly EphemerisDay[];
}

function toEphemerisPosition(dto: PlatformEphemerisDailyPositionDto | undefined): EphemerisPosition {
  return {
    longitude: dto?.longitude ?? 0,
    retrograde: dto?.retrograde ?? false,
    gate: dto?.gate ?? 0,
    line: dto?.line ?? 0,
    color: dto?.color ?? 0,
    tone: dto?.tone ?? 0,
    base: dto?.base ?? 0,
  };
}

/** DTO → model; gooit bij onbekende kolommen of dagen die niet bij de kolommen passen. */
export function toEphemerisDailyTable(dto: PlatformEphemerisDailyResponse): EphemerisDailyTable {
  const bodies = (dto.bodies ?? []).map(body => body.key ?? '');
  if (bodies.length === 0 || !bodies.every(isEphemerisBodyKey)) {
    throw new Error(`Onbekende ephemeris-kolommen: ${bodies.join(', ')}`);
  }
  const days = (dto.days ?? []).map(day => {
    const positions = day.positions ?? [];
    if (positions.length !== bodies.length) {
      throw new Error('Ephemeris-dagposities passen niet bij de kolommen');
    }
    return {
      atUtc: moment.utc(day.atUtc),
      positions: positions.map(toEphemerisPosition),
      moonLater: toEphemerisPosition(day.moonLater),
    };
  });

  return {
    fromUtc: moment.utc(dto.fromUtc),
    toUtc: moment.utc(dto.toUtc),
    minUtc: moment.utc(dto.minUtc),
    maxUtc: moment.utc(dto.maxUtc),
    maxWindowDays: dto.maxWindowDays ?? 31,
    stepHours: dto.stepHours ?? 12,
    moonOffsetHours: dto.moonOffsetHours ?? 6,
    timeZone: dto.timeZone ?? 'UTC',
    bodies,
    days,
  };
}

/** Graden per lichaam, of `undefined` als ze ontbreken of niet bij de kolommen passen (dan geen tooltip). */
function longitudesFor(values: readonly number[] | null | undefined, count: number): readonly number[] | undefined {
  return values && values.length === count ? values : undefined;
}

/** Alle lichamen op één (door de gebruiker ingevoegd) moment, de Maan ook `moonOffsetHours` later. */
export interface EphemerisMoment {
  readonly atUtc: Moment;
  /** Zelfde volgorde als {@link EPHEMERIS_BODY_KEYS}. */
  readonly positions: readonly EphemerisPosition[];
  readonly moonLater: EphemerisPosition;
}

/** DTO → model; gooit als de posities niet bij de vaste kolommen passen. */
export function toEphemerisMoment(dto: PlatformEphemerisMomentResponse): EphemerisMoment {
  const positions = dto.positions ?? [];
  if (positions.length !== EPHEMERIS_BODY_KEYS.length) {
    throw new Error('Ephemeris-moment past niet bij de kolommen');
  }
  return {
    atUtc: moment.utc(dto.atUtc),
    positions: positions.map(toEphemerisPosition),
    moonLater: toEphemerisPosition(dto.moonLater),
  };
}

/**
 * Unicode-symbolen per lichaam voor export (CSV/Excel), waar het Astronomicon-font van de
 * app niet beschikbaar is. Zelfde set als de Excel-export van de backend-tool.
 */
export const EPHEMERIS_UNICODE_GLYPHS: Readonly<Record<EphemerisBodyKey, string>> = {
  sun: '☉', earth: '⊕', 'north-node': '☊', 'south-node': '☋', moon: '☽', mercury: '☿', venus: '♀',
  mars: '♂', jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇', chiron: '⚷', lilith: '⚸',
};
